import {
  createClient,
  type SupabaseClient,
  type User,
} from '@supabase/supabase-js';

import { env } from './env';
import type { Database } from './database.types';

/**
 * Singleton Supabase client used across the app.
 *
 * Auth state is persisted in `localStorage` (the SDK default for browser
 * environments) and refreshed automatically.
 *
 * `flowType: 'implicit'` is used (instead of PKCE) so the same magic link
 * can be opened in a different browser than the one that initiated the
 * sign-in. PKCE stores the code_verifier in the initiator's localStorage
 * and would break the cross-device invite flow (Owner sends invite from
 * their browser → invitee opens it in theirs). Implicit returns the
 * session directly in the URL hash; for a personal tool this is fine.
 */
export const supabase: SupabaseClient<Database> = createClient<Database>(
  env.supabaseUrl,
  env.supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'implicit',
    },
  },
);

/**
 * Resolve the signed-in user (or `null` when no session is active).
 * Wraps the `{ data, error }` shape so callers don't repeat the
 * destructure + throw at every call site.
 */
export const currentUser = async (): Promise<User | null> => {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data.user;
};

/** Variant that throws when no user is signed in. Use at sites that
 *  cannot proceed without a user (writes, identity-bearing reads). */
export const requireCurrentUser = async (): Promise<User> => {
  const user = await currentUser();
  if (!user) throw new Error('You must be signed in.');
  return user;
};
